import { useState, useEffect } from "react";
import { userService, User } from "../../services/userService";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { PermissionsSelector } from "../users/PermissionsSelector";
import { Permission } from "../../types";

interface EditAdminDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin: User | null;
}

export function EditAdminDialog({
  isOpen,
  onClose,
  onSuccess,
  admin,
}: EditAdminDialogProps) {
  const { user: currentUser, isSuperAdmin: currentUserIsSuperAdmin } = useAuth();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditingSelf = admin?._id === currentUser?._id;
  const canEditPermissions = currentUserIsSuperAdmin && !isEditingSelf;

  useEffect(() => {
    if (admin) {
      setFormData({
        username: admin.username || "",
        email: admin.email || "",
        firstName: admin.firstName || "",
        lastName: admin.lastName || "",
      });
      setSelectedPermissions(admin.permissions || []);
      setIsSuperAdmin(admin.isSuperAdmin || false);
    }
  }, [admin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!admin) return;

    setIsLoading(true);
    setError(null);

    try {
      const updateData: any = {
        username: formData.username,
        firstName: formData.firstName,
        lastName: formData.lastName,
      };

      // Only super admins can update admin permissions
      if (canEditPermissions) {
        updateData.permissions = isSuperAdmin ? [] : selectedPermissions;
        updateData.isSuperAdmin = isSuperAdmin;
      }

      await userService.updateUser(admin._id, updateData);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Update admin error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update admin. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Admin">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="text-sm font-medium text-gray-700"
            >
              First Name
            </label>
            <Input
              id="firstName"
              placeholder="Admin's first name"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="text-sm font-medium text-gray-700"
            >
              Last Name
            </label>
            <Input
              id="lastName"
              placeholder="Admin's last name"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="username"
            className="text-sm font-medium text-gray-700"
          >
            Username
          </label>
          <Input
            id="username"
            placeholder="Choose a username"
            value={formData.username}
            onChange={handleChange}
            required
            autoComplete="username"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address
          </label>
          <Input
            id="email"
            type="email"
            placeholder="admin@example.com"
            value={formData.email}
            onChange={handleChange}
            required
            disabled // Email is usually not editable this way to avoid auth issues
            className="bg-gray-50 opacity-70"
            autoComplete="email"
          />
          <p className="text-[10px] text-gray-400">
            Email cannot be changed from this dialog.
          </p>
        </div>

        {/* Admin permissions section */}
        {admin?.role === "admin" && (
          <>
            {isEditingSelf && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  You cannot modify your own permissions or super admin status.
                </p>
              </div>
            )}

            <div className="space-y-2">
              {canEditPermissions && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="isSuperAdmin"
                    checked={isSuperAdmin}
                    onChange={(e) => {
                      setIsSuperAdmin(e.target.checked);
                      if (e.target.checked) {
                        setSelectedPermissions([]);
                      }
                    }}
                    disabled={isEditingSelf}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <label
                      htmlFor="isSuperAdmin"
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      Super Admin
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Super admins have all permissions and can manage other admins.
                    </p>
                  </div>
                </div>
              )}

              {canEditPermissions && !isSuperAdmin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <div className="max-h-64 overflow-y-auto">
                    <PermissionsSelector
                      selectedPermissions={selectedPermissions}
                      onChange={setSelectedPermissions}
                      compact
                    />
                  </div>
                </div>
              )}

              {!canEditPermissions && !isSuperAdmin && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    {selectedPermissions.length} permissions assigned
                  </div>
                </div>
              )}

              {isSuperAdmin && !canEditPermissions && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    All Permissions
                  </div>
                </div>
              )}
            </div>
          </>
        )}


        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="w-32"
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
